/**
 * TUVI - Módulo de Cuentas de Cobro
 * Generación de cuentas de cobro con plantilla corporativa
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import html2pdf from 'html2pdf.js';
import './CuentasCobro.css';

// Función para obtener fecha actual en zona horaria de Colombia
const getFechaColombia = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
};

// Función para convertir número a letras (colombiano)
const numeroALetras = (numero) => {
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const especiales = ['ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  const convertirGrupo = (n) => {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';

    let resultado = '';
    const c = Math.floor(n / 100);
    const resto = n % 100;
    const d = Math.floor(resto / 10);
    const u = resto % 10;

    if (c > 0) resultado += centenas[c] + ' ';

    if (resto === 0) return resultado.trim();

    if (resto >= 11 && resto <= 19) {
      return (resultado + especiales[resto - 11]).trim();
    }

    if (d === 2 && u > 0) {
      return (resultado + 'VEINTI' + unidades[u]).trim();
    }

    if (d > 0) {
      resultado += decenas[d];
      if (u > 0) resultado += ' Y ';
    }

    if (u > 0) resultado += unidades[u];

    return resultado.trim();
  };

  if (numero === 0) return 'CERO PESOS M/CTE';

  const num = Math.floor(numero);
  let resultado = '';

  const millones = Math.floor(num / 1000000);
  const miles = Math.floor((num % 1000000) / 1000);
  const unidadesNum = num % 1000;

  if (millones > 0) {
    if (millones === 1) {
      resultado += 'UN MILLÓN ';
    } else {
      resultado += convertirGrupo(millones) + ' MILLONES ';
    }
  }

  if (miles > 0) {
    if (miles === 1) {
      resultado += 'MIL ';
    } else {
      resultado += convertirGrupo(miles) + ' MIL ';
    }
  }

  if (unidadesNum > 0) {
    resultado += convertirGrupo(unidadesNum);
  }

  return resultado.trim() + ' PESOS M/CTE';
};

const CuentasCobro = () => {
  const { success, error } = useNotification();
  const { tienePermiso } = useAuth();
  const puedeValidarPago = tienePermiso('documentos', 'cuentas-cobro-confirmar');
  const documentRef = useRef(null);

  // Modal "Relacionar pago" a una cuenta de cobro
  const [modalPago, setModalPago] = useState(null); // cuenta de cobro seleccionada
  const [camposPago, setCamposPago] = useState([]);
  const [cargandoCampos, setCargandoCampos] = useState(false);
  const [pagoForm, setPagoForm] = useState({ campo_pago_id: '', monto: '', fecha: '', existentes: [], nuevos: [] });
  const [guardandoPago, setGuardandoPago] = useState(false);
  const [confirmandoId, setConfirmandoId] = useState(null);

  const [vista, setVista] = useState('lista'); // lista, crear, editar
  const [cuentas, setCuentas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cuentaEditando, setCuentaEditando] = useState(null);
  const [pendienteDescarga, setPendienteDescarga] = useState(null);

  // Estado de búsqueda para lista principal
  const [busquedaLista, setBusquedaLista] = useState('');

  // Estado de búsqueda para dropdowns
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [busquedaContrato, setBusquedaContrato] = useState('');
  const [showProyectoDropdown, setShowProyectoDropdown] = useState(false);
  const [showContratoDropdown, setShowContratoDropdown] = useState(false);
  const proyectoDropdownRef = useRef(null);
  const contratoDropdownRef = useRef(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    fecha: getFechaColombia(),
    proyecto_id: '',
    contrato_id: '',
    nombre_cliente: '',
    cedula_cliente: '',
    valor_pago: '',
    tipo_pago: 'anticipo',
    valor_contrato: '',
    proyecto_nombre: '',
    apartamento: ''
  });

  // Valores calculados
  const [valorPagoLetras, setValorPagoLetras] = useState('');
  const [valorContratoLetras, setValorContratoLetras] = useState('');

  const abortRef = useRef(null);

  useEffect(() => {
    cargarCuentas();
    cargarProyectos();
    cargarContratos();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (proyectoDropdownRef.current && !proyectoDropdownRef.current.contains(e.target)) {
        setShowProyectoDropdown(false);
      }
      if (contratoDropdownRef.current && !contratoDropdownRef.current.contains(e.target)) {
        setShowContratoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Actualizar letras cuando cambian los valores
  useEffect(() => {
    if (formData.valor_pago) {
      setValorPagoLetras(numeroALetras(parseFloat(formData.valor_pago)));
    } else {
      setValorPagoLetras('');
    }
  }, [formData.valor_pago]);

  useEffect(() => {
    if (formData.valor_contrato) {
      setValorContratoLetras(numeroALetras(parseFloat(formData.valor_contrato)));
    } else {
      setValorContratoLetras('');
    }
  }, [formData.valor_contrato]);

  // Auto-descargar PDF cuando se carga una cuenta desde el listado
  useEffect(() => {
    if (pendienteDescarga && vista === 'editar') {
      const timer = setTimeout(async () => {
        await descargarPDF(pendienteDescarga);
        setPendienteDescarga(null);
        setVista('lista');
        resetForm();
      }, 500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendienteDescarga, vista]);

  const cargarCuentas = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const data = await client.query(`{ cuentasCobro { id consecutivo fecha nombre_cliente cedula_cliente valor_pago valor_pago_letras tipo_pago valor_contrato valor_contrato_letras proyecto_id proyecto_nombre apartamento archivo_pdf created_at pago_archivo pago_nombre_archivo pago_archivos { key nombre } pago_monto pago_fecha pago_campo_id pago_campo_nombre pago_confirmado pago_confirmado_por pago_confirmado_fecha } }`);
      setCuentas(data.cuentasCobro || []);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error al cargar cuentas:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarProyectos = async () => {
    try {
      const data = await client.query(`{ proyectos { id proyecto_nombre nombre_cliente apartamento cedula telefono correo valor_final } }`);
      setProyectos(data.proyectos || []);
    } catch (err) {
      console.error('Error al cargar proyectos:', err);
    }
  };

  const cargarContratos = async () => {
    try {
      const data = await client.query(`{ contratos { id consecutivo nombre_cliente cedula_cliente proyecto_nombre apartamento valor_contrato } }`);
      setContratos(data.contratos || []);
    } catch (err) {
      console.error('Error al cargar contratos:', err);
    }
  };

  const seleccionarProyecto = (proyecto) => {
    setFormData(prev => ({
      ...prev,
      proyecto_id: proyecto.id,
      contrato_id: '',
      nombre_cliente: proyecto.nombre_cliente || '',
      cedula_cliente: proyecto.cedula_cliente || '',
      valor_contrato: proyecto.valor_final || '',
      proyecto_nombre: proyecto.proyecto_nombre || '',
      apartamento: proyecto.apartamento || ''
    }));
    setBusquedaProyecto(`${proyecto.proyecto_nombre} - ${proyecto.nombre_cliente}`);
    setBusquedaContrato('');
    setShowProyectoDropdown(false);
  };

  const seleccionarContrato = (contrato) => {
    setFormData(prev => ({
      ...prev,
      contrato_id: contrato.id,
      proyecto_id: contrato.proyecto_id || '',
      nombre_cliente: contrato.nombre_cliente || '',
      cedula_cliente: contrato.cedula_cliente || '',
      valor_contrato: contrato.valor_contrato || '',
      proyecto_nombre: contrato.proyecto_nombre || '',
      apartamento: contrato.apartamento || ''
    }));
    setBusquedaContrato(`#${contrato.consecutivo} - ${contrato.proyecto_nombre} - ${contrato.nombre_cliente}`);
    setBusquedaProyecto('');
    setShowContratoDropdown(false);
  };

  const limpiarSeleccion = () => {
    setFormData(prev => ({
      ...prev,
      proyecto_id: '',
      contrato_id: '',
      nombre_cliente: '',
      cedula_cliente: '',
      valor_contrato: '',
      proyecto_nombre: '',
      apartamento: ''
    }));
    setBusquedaProyecto('');
    setBusquedaContrato('');
  };

  // Filtrar proyectos por búsqueda
  const proyectosFiltrados = proyectos.filter(p => {
    if (!busquedaProyecto) return true;
    const texto = busquedaProyecto.toLowerCase();
    return (p.proyecto_nombre || '').toLowerCase().includes(texto) ||
           (p.nombre_cliente || '').toLowerCase().includes(texto) ||
           (p.apartamento || '').toLowerCase().includes(texto);
  });

  // Filtrar contratos por búsqueda
  const contratosFiltrados = contratos.filter(c => {
    if (!busquedaContrato) return true;
    const texto = busquedaContrato.toLowerCase();
    return (c.proyecto_nombre || '').toLowerCase().includes(texto) ||
           (c.nombre_cliente || '').toLowerCase().includes(texto) ||
           (c.apartamento || '').toLowerCase().includes(texto) ||
           String(c.consecutivo).includes(texto);
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      fecha: getFechaColombia(),
      proyecto_id: '',
      contrato_id: '',
      nombre_cliente: '',
      cedula_cliente: '',
      valor_pago: '',
      tipo_pago: 'anticipo',
      valor_contrato: '',
      proyecto_nombre: '',
      apartamento: ''
    });
    setCuentaEditando(null);
    setBusquedaProyecto('');
    setBusquedaContrato('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.nombre_cliente || !formData.cedula_cliente) {
      error('Ingrese los datos del cliente');
      return;
    }
    if (!formData.valor_pago || parseFloat(formData.valor_pago) <= 0) {
      error('Ingrese un valor de pago válido');
      return;
    }
    if (!formData.valor_contrato || parseFloat(formData.valor_contrato) <= 0) {
      error('Ingrese un valor de contrato válido');
      return;
    }
    if (!formData.proyecto_nombre || !formData.apartamento) {
      error('Ingrese el proyecto y apartamento');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        valor_pago_letras: valorPagoLetras,
        valor_contrato_letras: valorContratoLetras
      };

      if (cuentaEditando) {
        payload.id = cuentaEditando.id;
      }

      const gqlInput = {
        fecha: payload.fecha, nombre_cliente: payload.nombre_cliente,
        cedula_cliente: payload.cedula_cliente, valor_pago: parseFloat(payload.valor_pago) || 0,
        valor_pago_letras: payload.valor_pago_letras, tipo_pago: payload.tipo_pago,
        valor_contrato: parseFloat(payload.valor_contrato) || 0,
        valor_contrato_letras: payload.valor_contrato_letras,
        proyecto_id: payload.proyecto_id ? parseInt(payload.proyecto_id) : null,
        proyecto_nombre: payload.proyecto_nombre, apartamento: payload.apartamento || '',
      };

      let result;
      if (cuentaEditando) {
        const gqlR = await client.mutate(
          `mutation($id: Int!, $input: CrearCuentaCobroInput!) { editarCuentaCobro(id: $id, input: $input) { success message id consecutivo } }`,
          { id: cuentaEditando.id, input: gqlInput }
        );
        result = gqlR.editarCuentaCobro;
      } else {
        const gqlR = await client.mutate(
          `mutation($input: CrearCuentaCobroInput!) { crearCuentaCobro(input: $input) { success message id consecutivo } }`,
          { input: gqlInput }
        );
        result = gqlR.crearCuentaCobro;
      }

      if (result.success) {
        success(cuentaEditando ? 'Cuenta actualizada' : `Cuenta de cobro #${result.consecutivo} creada`);
        cargarCuentas();
        setVista('lista');
        resetForm();
      } else {
        error(result.message || 'Error al guardar');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const editarCuenta = (cuenta) => {
    setCuentaEditando(cuenta);
    setFormData({
      fecha: cuenta.fecha,
      proyecto_id: cuenta.proyecto_id || '',
      contrato_id: cuenta.contrato_id || '',
      nombre_cliente: cuenta.nombre_cliente,
      cedula_cliente: cuenta.cedula_cliente,
      valor_pago: cuenta.valor_pago,
      tipo_pago: cuenta.tipo_pago,
      valor_contrato: cuenta.valor_contrato,
      proyecto_nombre: cuenta.proyecto_nombre,
      apartamento: cuenta.apartamento
    });
    // Restaurar texto de búsqueda
    if (cuenta.proyecto_id) {
      const proy = proyectos.find(p => p.id === parseInt(cuenta.proyecto_id));
      if (proy) setBusquedaProyecto(`${proy.proyecto_nombre} - ${proy.nombre_cliente}`);
    }
    if (cuenta.contrato_id) {
      const cont = contratos.find(c => c.id === parseInt(cuenta.contrato_id));
      if (cont) setBusquedaContrato(`#${cont.consecutivo} - ${cont.proyecto_nombre} - ${cont.nombre_cliente}`);
    }
    setVista('editar');
  };

  const handleEliminarCuenta = async (cuenta) => {
    if (!window.confirm(`¿Eliminar cuenta de cobro #${cuenta.consecutivo}?`)) return;
    try {
      const gqlR = await client.mutate(
        `mutation($id: Int!) { eliminarCuentaCobro(id: $id) { success message } }`,
        { id: cuenta.id }
      );
      if (gqlR.eliminarCuentaCobro.success) {
        success('Cuenta de cobro eliminada');
        cargarCuentas();
      } else {
        error(gqlR.eliminarCuentaCobro.message);
      }
    } catch (err) {
      error('Error al eliminar');
    }
  };

  const descargarPDFDesdeListado = (cuenta) => {
    editarCuenta(cuenta);
    setPendienteDescarga(cuenta);
  };

  // ============================================
  // RELACIONAR / VALIDAR PAGO
  // ============================================
  const abrirModalPago = async (cuenta) => {
    setModalPago(cuenta);
    setPagoForm({
      campo_pago_id: cuenta.pago_campo_id ? String(cuenta.pago_campo_id) : '',
      monto: cuenta.pago_monto ? String(cuenta.pago_monto) : String(cuenta.valor_pago || ''),
      fecha: cuenta.pago_fecha || getFechaColombia(),
      existentes: Array.isArray(cuenta.pago_archivos) ? cuenta.pago_archivos.map(a => ({ key: a.key, nombre: a.nombre })) : [],
      nuevos: [],
    });
    setCamposPago([]);
    setCargandoCampos(true);
    try {
      const data = await client.query(
        `query($id: Int!) { carteraCamposCuentaCobro(cuenta_cobro_id: $id) { success message campos { id nombre porcentaje valor_calculado valor_recibido estado_cobro pendiente } } }`,
        { id: cuenta.id }
      );
      const r = data.carteraCamposCuentaCobro;
      if (!r.success) error(r.message || 'No se encontró la cartera del proyecto');
      setCamposPago(r.campos || []);
    } catch (err) {
      error('Error cargando campos de cartera');
    } finally {
      setCargandoCampos(false);
    }
  };

  const cerrarModalPago = () => {
    setModalPago(null);
    setCamposPago([]);
    setPagoForm({ campo_pago_id: '', monto: '', fecha: '', existentes: [], nuevos: [] });
  };

  const guardarPagoRelacionado = async () => {
    if (!pagoForm.campo_pago_id) { error('Elige a qué campo de pago aplica'); return; }
    if (!pagoForm.monto || parseFloat(pagoForm.monto) <= 0) { error('Ingresa un monto válido'); return; }
    setGuardandoPago(true);
    try {
      // Subir los comprobantes nuevos (uno o varios) al bucket de comprobantes
      const token = localStorage.getItem('tuvi_token');
      const subidos = [];
      for (const f of pagoForm.nuevos) {
        const fd = new FormData();
        fd.append('files', f);
        const upRes = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=cuentas-cobro/pagos/&bucket=comprobantes`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        const upData = await upRes.json();
        if (upData.files?.[0]?.key) subidos.push({ key: upData.files[0].key, nombre: f.name });
      }
      // Lista final = los ya guardados que se conservan + los nuevos subidos
      const archivos = [...pagoForm.existentes, ...subidos];
      const gqlR = await client.mutate(
        `mutation($input: RelacionarPagoInput!) { relacionarPagoCuentaCobro(input: $input) { success message } }`,
        { input: {
          cuenta_cobro_id: modalPago.id,
          campo_pago_id: parseInt(pagoForm.campo_pago_id),
          monto: parseFloat(pagoForm.monto),
          fecha: pagoForm.fecha || null,
          archivos,
        } }
      );
      if (gqlR.relacionarPagoCuentaCobro.success) {
        success('Pago relacionado (pendiente de validación)');
        cerrarModalPago();
        cargarCuentas();
      } else {
        error(gqlR.relacionarPagoCuentaCobro.message);
      }
    } catch (err) {
      error('Error al relacionar el pago');
    } finally {
      setGuardandoPago(false);
    }
  };

  const toggleValidarPago = async (cuenta) => {
    if (!puedeValidarPago) return;
    const nuevoValor = !cuenta.pago_confirmado;
    if (nuevoValor && !window.confirm(`¿Confirmas que el pago de la cuenta #${cuenta.consecutivo} fue validado? Se reflejará en cartera.`)) return;
    if (!nuevoValor && !window.confirm(`¿Quitar la validación del pago de la cuenta #${cuenta.consecutivo}? Se revertirá en cartera.`)) return;
    setConfirmandoId(cuenta.id);
    try {
      const gqlR = await client.mutate(
        `mutation($id: Int!, $c: Boolean!) { confirmarPagoCuentaCobro(cuenta_cobro_id: $id, confirmado: $c) { success message } }`,
        { id: cuenta.id, c: nuevoValor }
      );
      if (gqlR.confirmarPagoCuentaCobro.success) {
        success(gqlR.confirmarPagoCuentaCobro.message);
        cargarCuentas();
      } else {
        error(gqlR.confirmarPagoCuentaCobro.message);
      }
    } catch (err) {
      error('Error al validar el pago');
    } finally {
      setConfirmandoId(null);
    }
  };

  const descargarPDF = async (cuenta = null) => {
    const cuentaData = cuenta || {
      consecutivo: 'XXX',
      fecha: formData.fecha,
      nombre_cliente: formData.nombre_cliente,
      cedula_cliente: formData.cedula_cliente,
      valor_pago: formData.valor_pago,
      valor_pago_letras: valorPagoLetras,
      tipo_pago: formData.tipo_pago,
      valor_contrato: formData.valor_contrato,
      valor_contrato_letras: valorContratoLetras,
      proyecto_nombre: formData.proyecto_nombre,
      apartamento: formData.apartamento
    };

    // Usar el elemento de vista previa que ya está renderizado
    const previewElement = documentRef.current?.querySelector('.documento-preview');

    if (!previewElement) {
      error('Error: No se encontró la vista previa');
      return;
    }

    const opt = {
      margin: 0,
      filename: `CTA COBRO-${cuentaData.consecutivo}-${cuentaData.tipo_pago.toUpperCase()}-${cuentaData.nombre_cliente}-${cuentaData.proyecto_nombre}-${cuentaData.apartamento}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        windowWidth: 612,
        windowHeight: 792
      },
      jsPDF: {
        unit: 'px',
        format: [612, 792],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      }
    };

    try {
      await html2pdf().set(opt).from(previewElement).save();
      success('PDF descargado correctamente');
    } catch (err) {
      console.error('Error PDF:', err);
      error('Error al generar PDF');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha + 'T00:00:00');
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${d.getDate()} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
  };

  const getTipoPagoTexto = (tipo) => {
    const tipos = {
      'separacion': 'Separación',
      'anticipo': 'Anticipo',
      'avance': 'Avance',
      'entrega_final': 'Entrega Final'
    };
    return tipos[tipo] || tipo;
  };

  // Generar HTML del documento para PDF
  const generarHTMLDocumento = (cuenta) => {
    const baseUrl = window.location.origin;
    return `
      <div style="width: 216mm; min-height: 279mm; font-family: 'Segoe UI', Arial, sans-serif; position: relative; background-color: #ffffff; box-sizing: border-box; color: #000000;">
        <!-- Header -->
        <div style="background-color: #f1f9dc; padding: 15px 30px; display: flex; justify-content: space-between; align-items: center;">
          <img src="${baseUrl}/logo.png" alt="TUVI" style="height: 40px;" crossorigin="anonymous" />
          <div style="color: #2d5016; font-size: 12px; font-weight: 500;">Facilidad  •  Seguridad  •  Calidad</div>
        </div>

        <!-- Content -->
        <div style="padding: 40px 50px 100px 50px; color: #000000;">
          <!-- Fecha y número -->
          <div style="text-align: right; margin-bottom: 30px; color: #000000;">
            <p style="margin: 0; font-size: 14px; color: #000000;">Medellín</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000000;">${formatDate(cuenta.fecha)}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000000;">Cuenta de cobro N° ${cuenta.consecutivo}</p>
          </div>

          <!-- Destinatario -->
          <div style="margin-bottom: 25px; color: #000000;">
            <p style="margin: 0; font-size: 14px; color: #000000;">Señor(a):</p>
            <p style="margin: 5px 0; font-size: 14px; font-weight: bold; color: #000000;">${cuenta.nombre_cliente}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000000;">CC: ${cuenta.cedula_cliente}</p>
          </div>

          <!-- Debe a -->
          <div style="margin-bottom: 25px; color: #000000;">
            <p style="margin: 0; font-size: 14px; font-weight: bold; color: #000000;">DEBE A:</p>
            <p style="margin: 5px 0; font-size: 14px; font-weight: bold; color: #000000;">TUVI CONSTRUCCIONES S.A.S</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000000;">Nit. 901771781-1</p>
          </div>

          <!-- Cuerpo -->
          <div style="margin-bottom: 40px; text-align: left; line-height: 1.6; color: #000000;">
            <p style="margin: 0; font-size: 14px; color: #000000;">
              La suma de <span style="font-weight: bold; color: #000000;">${cuenta.valor_pago_letras}</span> (<span style="font-weight: bold; color: #000000;">${formatMoney(cuenta.valor_pago)}</span>), correspondiente a la <span style="font-style: italic; color: #000000;">${getTipoPagoTexto(cuenta.tipo_pago)}</span> del valor del contrato y cotización aprobada por un valor total de <span style="font-weight: bold; color: #000000;">${cuenta.valor_contrato_letras}</span> (<span style="font-weight: bold; color: #000000;">${formatMoney(cuenta.valor_contrato)}</span>) Por concepto de la obra civil y remodelación del Proyecto <span style="text-decoration: underline; color: #000000;">${cuenta.proyecto_nombre} ${cuenta.apartamento}</span>
            </p>
          </div>

          <!-- Despedida -->
          <p style="margin: 30px 0 40px 0; font-size: 14px; color: #000000;">Cordialmente.</p>

          <!-- Firma -->
          <div style="margin-top: 20px; color: #000000; text-align: left;">
            <img src="${baseUrl}/firma juan esteban.png" alt="Firma" style="height: 60px; margin-bottom: 5px;" crossorigin="anonymous" />
            <p style="margin: 0; font-size: 14px; font-weight: bold; color: #000000;">Juan Esteban Duque Ríos</p>
            <p style="margin: 3px 0; font-size: 14px; color: #000000;">CC. 1.152.443.928</p>
            <p style="margin: 3px 0; font-size: 14px; font-weight: bold; color: #000000;">Gerente</p>
            <p style="margin: 3px 0; font-size: 14px; font-weight: bold; color: #000000;">TUVI CONSTRUCCIONES S.A.S</p>
            <p style="margin: 3px 0; font-size: 14px; color: #000000;">Contacto: +57 300 4758672</p>
          </div>

          <!-- Nota bancaria -->
          <p style="margin-top: 30px; font-size: 12px; color: #555555;">
            *Por favor consignar a la cuenta bancaria 37900005821 ahorros Bancolombia a nombre de TUVI CONSTRUCCIONES S.A.S
          </p>
        </div>

        <!-- Footer -->
        <div style="position: absolute; bottom: 0; left: 0; right: 0; background-color: #f1f9dc; padding: 15px 30px; display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #2d5016; font-size: 13px; font-weight: 500;">Tuvi Construcciones S.A.S</span>
          <div style="display: flex; gap: 20px; align-items: center;">
            <span style="color: #2d5016; font-size: 12px;">tuviconstrucciones.com</span>
            <span style="color: #2d5016; font-size: 12px;">@tuviacabados</span>
          </div>
        </div>
      </div>
    `;
  };

  return (
    <div className="cuentas-cobro-container">
      {/* Header */}
      <div className="cc-header">
        <div className="cc-header-info">
          <h1>📄 Cuentas de Cobro</h1>
          <p>Genera y administra cuentas de cobro para clientes</p>
        </div>
        <div className="cc-header-actions">
          {vista === 'lista' ? (
            <button className="btn-nueva-cuenta" onClick={() => { resetForm(); setVista('crear'); }}>
              + Nueva Cuenta de Cobro
            </button>
          ) : (
            <button className="btn-volver" onClick={() => { setVista('lista'); resetForm(); }}>
              ← Volver al listado
            </button>
          )}
        </div>
      </div>

      {/* Vista Lista */}
      {vista === 'lista' && (
        <div className="cc-lista-view">
          {loading ? (
            <div className="cc-loading">Cargando...</div>
          ) : cuentas.length === 0 ? (
            <div className="cc-empty">
              <span>📄</span>
              <p>No hay cuentas de cobro generadas</p>
              <button onClick={() => setVista('crear')}>Crear primera cuenta</button>
            </div>
          ) : (
            <div className="cc-tabla">
              <div className="cc-busqueda-lista">
                <input
                  type="text"
                  placeholder="Buscar por consecutivo, cliente, proyecto, apartamento..."
                  value={busquedaLista}
                  onChange={(e) => setBusquedaLista(e.target.value)}
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Tipo Pago</th>
                    <th>Valor Pago</th>
                    <th>Pago / Validación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuentas.filter(c => {
                    if (!busquedaLista) return true;
                    const q = busquedaLista.toLowerCase();
                    return (
                      String(c.consecutivo).includes(q) ||
                      (c.nombre_cliente || '').toLowerCase().includes(q) ||
                      (c.proyecto_nombre || '').toLowerCase().includes(q) ||
                      (c.apartamento || '').toLowerCase().includes(q) ||
                      (c.tipo_pago || '').toLowerCase().includes(q)
                    );
                  }).map(cuenta => (
                    <tr key={cuenta.id}>
                      <td><span className="consecutivo">#{cuenta.consecutivo}</span></td>
                      <td>{new Date(cuenta.fecha + 'T00:00:00').toLocaleDateString('es-CO')}</td>
                      <td>
                        <div className="cliente-info">
                          <span className="nombre">{cuenta.nombre_cliente}</span>
                          <span className="cedula">CC: {cuenta.cedula_cliente}</span>
                        </div>
                      </td>
                      <td>
                        <div className="proyecto-info">
                          <span className="nombre">{cuenta.proyecto_nombre}</span>
                          <span className="apto">{cuenta.apartamento}</span>
                        </div>
                      </td>
                      <td><span className={`badge-tipo ${cuenta.tipo_pago}`}>{getTipoPagoTexto(cuenta.tipo_pago)}</span></td>
                      <td className="monto">{formatMoney(cuenta.valor_pago)}</td>
                      <td>
                        {cuenta.pago_monto != null ? (
                          <div className="cc-pago-cell">
                            <span className="cc-pago-monto">{formatMoney(cuenta.pago_monto)}</span>
                            {cuenta.pago_campo_nombre && <span className="cc-pago-campo">→ {cuenta.pago_campo_nombre}</span>}
                            {Array.isArray(cuenta.pago_archivos) && cuenta.pago_archivos.length > 0 && (
                              <div className="cc-pago-archivos">
                                {cuenta.pago_archivos.map((a, i) => (
                                  <a key={i} href={`${UPLOAD_URL}/catalogo/img/comprobantes/${a.key}`} target="_blank" rel="noopener noreferrer"
                                    className="cc-pago-archivo-link" title={a.nombre || 'Ver comprobante'}>
                                    📎 {i + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                            {cuenta.pago_confirmado ? (
                              <span className="cc-pago-estado validado" title={`Validado por ${cuenta.pago_confirmado_por || ''}${cuenta.pago_confirmado_fecha ? ' · ' + new Date(cuenta.pago_confirmado_fecha).toLocaleString('es-CO') : ''}`}>
                                ✓ Validado{cuenta.pago_confirmado_por ? ` · ${cuenta.pago_confirmado_por}` : ''}
                              </span>
                            ) : (
                              <span className="cc-pago-estado pendiente">⏳ Sin validar</span>
                            )}
                            <label className={`cc-check ${puedeValidarPago ? '' : 'disabled'}`} title={puedeValidarPago ? 'Marcar como validado' : 'No tienes permiso para validar'}>
                              <input
                                type="checkbox"
                                checked={!!cuenta.pago_confirmado}
                                disabled={!puedeValidarPago || confirmandoId === cuenta.id}
                                onChange={() => toggleValidarPago(cuenta)}
                              />
                              <span>Pago validado</span>
                            </label>
                          </div>
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>— sin pago —</span>
                        )}
                      </td>
                      <td className="acciones">
                        <button className="btn-relacionar-pago" onClick={() => abrirModalPago(cuenta)} title="Relacionar pago" disabled={cuenta.pago_confirmado}>
                          💵
                        </button>
                        <button className="btn-editar" onClick={() => editarCuenta(cuenta)} title="Editar">
                          ✏️
                        </button>
                        <button className="btn-descargar-pdf" onClick={() => descargarPDFDesdeListado(cuenta)} title="Descargar PDF">
                          📥
                        </button>
                        <button
                          className="btn-editar"
                          onClick={() => handleEliminarCuenta(cuenta)}
                          title="Eliminar"
                          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vista Crear/Editar */}
      {(vista === 'crear' || vista === 'editar') && (
        <div className="cc-crear-view">
          <div className="cc-grid">
            {/* Formulario */}
            <div className="cc-formulario">
              <h2>{vista === 'editar' ? 'Editar Cuenta de Cobro' : 'Nueva Cuenta de Cobro'}</h2>

              <form onSubmit={handleSubmit}>
                {/* Seleccionar desde Proyecto o Contrato */}
                <div className="form-section">
                  <h3>Cargar datos desde Proyecto o Contrato (Opcional)</h3>
                  <div className="form-row">
                    {/* Buscar Proyecto */}
                    <div className="form-group">
                      <label>Buscar Proyecto</label>
                      <div className="cc-search-dropdown" ref={proyectoDropdownRef}>
                        <input
                          type="text"
                          value={busquedaProyecto}
                          onChange={(e) => {
                            setBusquedaProyecto(e.target.value);
                            setShowProyectoDropdown(true);
                            if (!e.target.value) {
                              setFormData(prev => ({ ...prev, proyecto_id: '' }));
                            }
                          }}
                          onFocus={() => setShowProyectoDropdown(true)}
                          placeholder="Escriba para buscar proyecto..."
                          className="cc-search-input"
                        />
                        {formData.proyecto_id && (
                          <button type="button" className="cc-search-clear" onClick={limpiarSeleccion}>×</button>
                        )}
                        {showProyectoDropdown && (
                          <div className="cc-search-list">
                            {proyectosFiltrados.length === 0 ? (
                              <div className="cc-search-empty">Sin resultados</div>
                            ) : (
                              proyectosFiltrados.map(p => (
                                <div
                                  key={p.id}
                                  className={`cc-search-item ${formData.proyecto_id === String(p.id) ? 'selected' : ''}`}
                                  onClick={() => seleccionarProyecto(p)}
                                >
                                  <span className="cc-search-main">{p.proyecto_nombre}</span>
                                  <span className="cc-search-sub">{p.nombre_cliente} {p.apartamento ? `- ${p.apartamento}` : ''}</span>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Buscar Contrato */}
                    <div className="form-group">
                      <label>Buscar Contrato</label>
                      <div className="cc-search-dropdown" ref={contratoDropdownRef}>
                        <input
                          type="text"
                          value={busquedaContrato}
                          onChange={(e) => {
                            setBusquedaContrato(e.target.value);
                            setShowContratoDropdown(true);
                            if (!e.target.value) {
                              setFormData(prev => ({ ...prev, contrato_id: '' }));
                            }
                          }}
                          onFocus={() => setShowContratoDropdown(true)}
                          placeholder="Escriba para buscar contrato..."
                          className="cc-search-input"
                        />
                        {formData.contrato_id && (
                          <button type="button" className="cc-search-clear" onClick={limpiarSeleccion}>×</button>
                        )}
                        {showContratoDropdown && (
                          <div className="cc-search-list">
                            {contratosFiltrados.length === 0 ? (
                              <div className="cc-search-empty">Sin resultados</div>
                            ) : (
                              contratosFiltrados.map(c => (
                                <div
                                  key={c.id}
                                  className={`cc-search-item ${formData.contrato_id === String(c.id) ? 'selected' : ''}`}
                                  onClick={() => seleccionarContrato(c)}
                                >
                                  <span className="cc-search-main">#{c.consecutivo} - {c.proyecto_nombre}</span>
                                  <span className="cc-search-sub">{c.nombre_cliente} {c.apartamento ? `- ${c.apartamento}` : ''}</span>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Datos básicos */}
                <div className="form-section">
                  <h3>Datos de la Cuenta</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Fecha *</label>
                      <input
                        type="date"
                        name="fecha"
                        value={formData.fecha}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Tipo de Pago *</label>
                      <select
                        name="tipo_pago"
                        value={formData.tipo_pago}
                        onChange={handleChange}
                        required
                      >
                        <option value="separacion">Separación</option>
                        <option value="anticipo">Anticipo</option>
                        <option value="avance">Avance</option>
                        <option value="entrega_final">Entrega Final</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Datos cliente */}
                <div className="form-section">
                  <h3>Datos del Cliente</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nombre del Cliente *</label>
                      <input
                        type="text"
                        name="nombre_cliente"
                        value={formData.nombre_cliente}
                        onChange={handleChange}
                        placeholder="Nombre completo"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Cédula *</label>
                      <input
                        type="text"
                        name="cedula_cliente"
                        value={formData.cedula_cliente}
                        onChange={handleChange}
                        placeholder="Número de cédula"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Datos proyecto */}
                <div className="form-section">
                  <h3>Datos del Proyecto</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nombre del Proyecto *</label>
                      <input
                        type="text"
                        name="proyecto_nombre"
                        value={formData.proyecto_nombre}
                        onChange={handleChange}
                        placeholder="Ej: VILLA GABRIELA"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Apartamento *</label>
                      <input
                        type="text"
                        name="apartamento"
                        value={formData.apartamento}
                        onChange={handleChange}
                        placeholder="Ej: Apto 301"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Valores */}
                <div className="form-section">
                  <h3>Valores</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Valor del Pago *</label>
                      <input
                        type="number"
                        name="valor_pago"
                        value={formData.valor_pago}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        step="any"
                        required
                      />
                      {valorPagoLetras && (
                        <span className="valor-letras">{valorPagoLetras}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Valor Total del Contrato *</label>
                      <input
                        type="number"
                        name="valor_contrato"
                        value={formData.valor_contrato}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        step="any"
                        required
                      />
                      {valorContratoLetras && (
                        <span className="valor-letras">{valorContratoLetras}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancelar" onClick={() => { setVista('lista'); resetForm(); }}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-guardar" disabled={loading}>
                    {loading ? 'Guardando...' : (vista === 'editar' ? 'Actualizar' : 'Guardar')}
                  </button>
                </div>
              </form>
            </div>

            {/* Vista previa */}
            <div className="cc-preview">
              <h2>Vista Previa</h2>
              <div className="preview-container" ref={documentRef}>
                <div className="documento-preview">
                  {/* Header */}
                  <div className="doc-header">
                    <img src="/logo.png" alt="TUVI" className="doc-logo" />
                    <div className="doc-slogan">Facilidad • Seguridad • Calidad</div>
                  </div>

                  {/* Content */}
                  <div className="doc-content">
                    {/* Fecha */}
                    <div className="doc-fecha-section">
                      <p>Medellín</p>
                      <p>{formatDate(formData.fecha) || 'DD de MES de AAAA'}</p>
                      <p>Cuenta de cobro N° {cuentaEditando?.consecutivo || 'XXX'}</p>
                    </div>

                    {/* Destinatario */}
                    <div className="doc-destinatario">
                      <p>Señor(a):</p>
                      <p className="bold">{formData.nombre_cliente || 'NOMBRE CLIENTE'}</p>
                      <p>CC: {formData.cedula_cliente || 'CEDULA CLIENTE'}</p>
                    </div>

                    {/* Debe a */}
                    <div className="doc-debe">
                      <p className="bold">DEBE A:</p>
                      <p className="bold">TUVI CONSTRUCCIONES S.A.S</p>
                      <p>Nit. 901771781-1</p>
                    </div>

                    {/* Cuerpo */}
                    <div className="doc-cuerpo">
                      <p>
                        La suma de <span className="bold">{valorPagoLetras || 'VALOR EN LETRAS DEL PAGO'}</span> (<span className="bold">{formData.valor_pago ? formatMoney(formData.valor_pago) : '$VALOR EN NÚMEROS'}</span>), correspondiente a la <span className="italic">{getTipoPagoTexto(formData.tipo_pago)}</span> del valor del contrato y cotización aprobada por un valor total de <span className="bold">{valorContratoLetras || 'VALOR TOTAL DEL CONTRATO EN LETRAS'}</span> (<span className="bold">{formData.valor_contrato ? formatMoney(formData.valor_contrato) : '$VALOR TOTAL'}</span>) Por concepto de la obra civil y remodelación del Proyecto <span className="underline">{formData.proyecto_nombre || 'NOMBRE PROYECTO'} {formData.apartamento || 'Y NÚMERO DE APARTAMENTO'}</span>
                      </p>
                    </div>

                    {/* Despedida */}
                    <p className="doc-despedida">Cordialmente.</p>

                    {/* Firma */}
                    <div className="doc-firma">
                      <img src="/firma juan esteban.png" alt="Firma" className="firma-img" />
                      <p className="bold">Juan Esteban Duque Ríos</p>
                      <p>CC. 1.152.443.928</p>
                      <p className="bold">Gerente</p>
                      <p className="bold">TUVI CONSTRUCCIONES S.A.S</p>
                      <p>Contacto: +57 300 4758672</p>
                    </div>

                    {/* Nota */}
                    <p className="doc-nota">
                      *Por favor consignar a la cuenta bancaria 37900005821 ahorros Bancolombia a nombre de TUVI CONSTRUCCIONES S.A.S
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="doc-footer">
                    <span className="footer-empresa">Tuvi Construcciones S.A.S</span>
                    <div className="footer-contacto">
                      <span>🌐 tuviconstrucciones.com</span>
                      <span>📷 tuviacabados</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Relacionar pago a una cuenta de cobro */}
      {modalPago && (
        <div className="cc-modal-overlay" onClick={cerrarModalPago}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3>Relacionar pago · Cuenta #{modalPago.consecutivo}</h3>
              <button className="cc-modal-close" onClick={cerrarModalPago}>×</button>
            </div>
            <div className="cc-modal-body">
              <p className="cc-modal-sub">
                {modalPago.proyecto_nombre} {modalPago.apartamento} · {modalPago.nombre_cliente}
              </p>
              <p className="cc-modal-nota">
                El pago se reflejará en cartera <strong>solo cuando se valide</strong> con el check (requiere permiso).
              </p>

              <label className="cc-field-label">Aplicar al campo de pago</label>
              {cargandoCampos ? (
                <div className="cc-loading">Cargando campos de cartera…</div>
              ) : camposPago.length === 0 ? (
                <div className="cc-loading">No se encontraron campos de cartera para este proyecto/cliente.</div>
              ) : (
                <select
                  className="cc-select"
                  value={pagoForm.campo_pago_id}
                  onChange={(e) => setPagoForm({ ...pagoForm, campo_pago_id: e.target.value })}
                >
                  <option value="">— Selecciona el campo —</option>
                  {camposPago.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} ({c.porcentaje}%) · pendiente {formatMoney(c.pendiente)}
                    </option>
                  ))}
                </select>
              )}

              <div className="cc-field-row">
                <div>
                  <label className="cc-field-label">Monto del pago</label>
                  <input
                    type="number" className="cc-input"
                    value={pagoForm.monto}
                    onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="cc-field-label">Fecha</label>
                  <input
                    type="date" className="cc-input"
                    value={pagoForm.fecha}
                    onChange={(e) => setPagoForm({ ...pagoForm, fecha: e.target.value })}
                  />
                </div>
              </div>

              <label className="cc-field-label">Comprobantes (uno o varios, opcional)</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setPagoForm(prev => ({ ...prev, nuevos: [...prev.nuevos, ...files] }));
                  e.target.value = '';
                }}
              />
              {(pagoForm.existentes.length > 0 || pagoForm.nuevos.length > 0) && (
                <div className="cc-archivos-lista">
                  {pagoForm.existentes.map((a, i) => (
                    <div key={`ex-${i}`} className="cc-archivo-item">
                      <a href={`${UPLOAD_URL}/catalogo/img/comprobantes/${a.key}`} target="_blank" rel="noopener noreferrer">📎 {a.nombre || 'comprobante'}</a>
                      <button type="button" className="cc-archivo-quitar" title="Quitar"
                        onClick={() => setPagoForm(prev => ({ ...prev, existentes: prev.existentes.filter((_, j) => j !== i) }))}>✕</button>
                    </div>
                  ))}
                  {pagoForm.nuevos.map((f, i) => (
                    <div key={`nv-${i}`} className="cc-archivo-item cc-archivo-nuevo">
                      <span>🆕 {f.name}</span>
                      <button type="button" className="cc-archivo-quitar" title="Quitar"
                        onClick={() => setPagoForm(prev => ({ ...prev, nuevos: prev.nuevos.filter((_, j) => j !== i) }))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="cc-modal-footer">
              {modalPago.pago_monto != null && !modalPago.pago_confirmado && (
                <button
                  className="cc-btn-quitar"
                  onClick={async () => {
                    if (!window.confirm('¿Quitar el pago relacionado?')) return;
                    const gqlR = await client.mutate(`mutation($id: Int!) { quitarPagoRelacionadoCuentaCobro(cuenta_cobro_id: $id) { success message } }`, { id: modalPago.id });
                    if (gqlR.quitarPagoRelacionadoCuentaCobro.success) { success('Pago quitado'); cerrarModalPago(); cargarCuentas(); }
                    else error(gqlR.quitarPagoRelacionadoCuentaCobro.message);
                  }}
                >
                  Quitar pago
                </button>
              )}
              <button className="cc-btn-cancelar" onClick={cerrarModalPago}>Cancelar</button>
              <button className="cc-btn-guardar" onClick={guardarPagoRelacionado} disabled={guardandoPago}>
                {guardandoPago ? 'Guardando…' : 'Relacionar pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasCobro;
